import React, { useEffect, useState } from 'react';

// Vendor Selection Popup Component
const VendorSelectionPopup = ({ 
  isOpen, 
  onClose, 
  subcategory, 
  vendorsList, 
  onSelectVendor,
  isLoading
}) => {
  const [selectedVendor, setSelectedVendor] = useState(null);

  // Reset selected vendor when popup opens with new subcategory
  useEffect(() => {
    if (isOpen && subcategory) {
      setSelectedVendor(subcategory.vendor || null);
    }
  }, [isOpen, subcategory]);

  if (!isOpen) return null;

  const handleApply = () => {
    onSelectVendor(selectedVendor, subcategory);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-800">
            Select Brand for {subcategory?.product_sub_category_name}
          </h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F30F0F] mb-4"></div>
              <p className="text-gray-600">Loading vendors...</p>
            </div>
          ) : vendorsList && vendorsList.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {vendorsList.map(vendor => (
                <div 
                  key={vendor._id} 
                  className={`border rounded-lg p-3 cursor-pointer transition-all ${
                    selectedVendor?._id === vendor._id 
                      ? 'border-[#F30F0F] bg-[#FFF8F8]' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedVendor(vendor)}
                >
                  <div className="flex items-center">
                    {/* Vendor Image */}
                    <div className="w-14 h-14 bg-white rounded-md overflow-hidden mr-3 border border-gray-200 flex-shrink-0 flex items-center justify-center">
                      {vendor.image ? (
                        <img
                          src={process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES + vendor.image}
                          alt={vendor.name}
                          className="max-h-12 max-w-12 object-contain"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/56?text=Vendor";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <span className="text-xs text-gray-500">No image</span>
                        </div>
                      )}
                    </div>

                    {/* Vendor Details */}
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800">{vendor.name}</h4>
                      {vendor.company_name && (
                        <p className="text-sm text-gray-600">{vendor.company_name}</p>
                      )}
                      {vendor.city && vendor.state && (
                        <p className="text-xs text-gray-500 mt-1">{vendor.city}, {vendor.state}</p>
                      )}
                    </div>

                    {/* Selection indicator */}
                    <div className="ml-2">
                      {selectedVendor?._id === vendor._id ? (
                        <div className="w-6 h-6 rounded-full border-2 border-[#F30F0F] bg-[#F30F0F] flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-6 h-6 rounded-full border-2 border-gray-300"></div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-gray-600">No vendors available for this subcategory</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!selectedVendor || isLoading}
            className={`px-4 py-2 rounded-md text-white transition-colors ${
              !selectedVendor || isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-[#F30F0F] hover:bg-[#D00C0C]'
            }`}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

// Updated BrandCustomizationSection with integrated popup
function BrandCustomizationSection({
  doorSubcategories = [],
  preEngDoorSubcategories = [],
  quotationData,
  onVendorChange // Add this prop to handle vendor changes
}) {
  const [doorSubcatsWithVendor, setDoorSubcatsWithVendor] = useState([]);
  const [preEngDoorSubcatsWithVendor, setPreEngDoorSubcatsWithVendor] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [vendorsListPopupOpen, setVendorsListPopupOpen] = useState(false);
  const [subcategoryVendorsList, setSubcategoryVendorsList] = useState([]);
  const [isLoadingVendors, setIsLoadingVendors] = useState(false);

  // Process subcategories and map vendors from quotation data
  useEffect(() => {
    // Map vendors to door subcategories
    const mapVendorsToSubcategories = (subcategories) => {
      return subcategories.map(subcatData => {
        // Extract subcategory and vendors from the data structure
        const { subcategory, vendors = [], defaultVendor } = subcatData;
        
        if (!subcategory) return null;
        
        // Check if this subcategory is selected in quotation
        const isSelected = quotationData?.items?.some(item =>
          item.product_sub_category_id === subcategory._id
        );
        
        return {
          ...subcategory,
          vendors,
          vendor: defaultVendor || (vendors.length > 0 ? vendors[0] : null),
          isSelected
        };
      }).filter(Boolean); // Remove null entries
    };
    
    // Process regular door subcategories
    const processedDoorSubcats = mapVendorsToSubcategories(doorSubcategories);
    setDoorSubcatsWithVendor(processedDoorSubcats);
    
    // Process pre-engineered door subcategories
    const processedPreEngDoorSubcats = mapVendorsToSubcategories(preEngDoorSubcategories);
    setPreEngDoorSubcatsWithVendor(processedPreEngDoorSubcats);
  }, [doorSubcategories, preEngDoorSubcategories, quotationData]);

  const handleSubcategoryVendorChange = async (subcategory) => {
    setSelectedSubcategory(subcategory);
    setVendorsListPopupOpen(true);
    setIsLoadingVendors(true);
    setSubcategoryVendorsList([]);
    
    try {
      const result = await getSubcategoryVendors(subcategory._id);
      if (result.success) {
        setSubcategoryVendorsList(result.data || []);
      } else {
        console.error("Failed to fetch vendors:", result.message);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    } finally {
      setIsLoadingVendors(false);
    }
  };

  const handleSelectVendor = (vendor, subcategory) => {
    if (!subcategory || !vendor) return;

    // Update the vendor for the selected subcategory
    const updateSubcategories = (subcategories) => {
      return subcategories.map(subcat => {
        if (subcat._id === subcategory._id) {
          return { ...subcat, vendor };
        }
        return subcat;
      });
    };

    // Update both subcategory lists
    setDoorSubcatsWithVendor(updateSubcategories(doorSubcatsWithVendor));
    setPreEngDoorSubcatsWithVendor(updateSubcategories(preEngDoorSubcatsWithVendor));

    // Notify parent component about the vendor change
    if (onVendorChange) {
      onVendorChange(subcategory._id, vendor, subcategory.product_sub_category_name);
    }
  };

  const getSubcategoryVendors = async (product_sub_category_id) => {
    try {
      // Validate input
      if (!product_sub_category_id) {
        console.error("Error: product_sub_category_id is required");
        return { success: false, message: "Product subcategory ID is required" };
      }
      
      const token = sessionStorage.getItem('token');
      
      if (!token) {
        console.error("Authentication token not found");
        return { success: false, message: "Authentication token not found" };
      }
      
      // Make API call to the backend endpoint
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}vendor/get-vendorsBy-productSubCategoryId?product_sub_category_id=${product_sub_category_id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token,
        }
      });
      
      // Parse the JSON response
      const data = await response.json();
      console.log("Vendors:", data);
      
      // Check if the request was successful
      if (!response.ok) {
        console.error("API Error:", data.message || "Failed to fetch vendors");
        return {
          success: false,
          message: data.message || "Failed to fetch vendors",
          status: response.status
        };
      }
      
      // Return the successful response
      return {
        success: true,
        data: data.data, // The vendors array from the response
        message: data.message || "Vendors fetched successfully"
      };
    } catch (error) {
      console.error("Error fetching vendors:", error);
      return {
        success: false,
        message: "An unexpected error occurred while fetching vendors",
        error: error.message
      };
    }
  };

  const renderSubcategoryCard = (subcategory) => {
    return (
      <div key={subcategory._id} className="flex-shrink-0 w-[110px] flex flex-col items-center">
        {/* Subcategory name centered above the vendor div */}
        <h4 className="text-xs font-medium text-gray-700 truncate mb-1.5 text-center w-full flex items-center justify-center"
            title={subcategory.product_sub_category_name}>
          <span className="truncate">{subcategory.product_sub_category_name}</span>
          
          {/* Selection indicator - smaller and more subtle */}
          {subcategory.isSelected && (
            <div className="bg-[#F30F0F] rounded-full p-0.5 text-white flex-shrink-0 ml-1">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-2 w-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </h4>
        <div 
          className={`border rounded-lg p-2 cursor-pointer relative w-[90px] shadow-sm bg-white ${
            subcategory.isSelected ? 'border-[#F30F0F]' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          {/* Updated tick mark on the vendor div when selected - matching door type style */}
          {subcategory.vendor && (
            <div className="absolute top-1 right-1">
              <div className="w-3 h-3 rounded-full border border-[#C20C0C] bg-[#C20C0C] flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          )}

          {/* Vendor image and info - matching Figma layout */}
          {subcategory?.vendor ? (
            <div className="flex flex-col items-center">
              {/* Vendor image - sized per Figma */}
              <div className="w-12 h-12 bg-white rounded-md overflow-hidden mb-1 border border-gray-200 flex-shrink-0 flex items-center justify-center">
                {subcategory.vendor.image ? (
                  <img
                    src={process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES + subcategory.vendor.image}
                    alt={subcategory.vendor.name || "Vendor"}
                    className="max-h-10 max-w-10 object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/40?text=Vendor";
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <span className="text-[8px] text-gray-500">No img</span>
                  </div>
                )}
              </div>
              
              {/* Vendor name - matching Figma text size */}
              <p className="text-[10px] font-medium text-gray-700 text-center truncate w-full">
                {subcategory.vendor.name || "Brand"}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center text-xs text-gray-500 py-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mb-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-[10px]">No brand</span>
            </div>
          )}
        </div>

        {/* Change brand button outside the border - matching Figma */}
        <button
          className="text-[10px] border border-[#F30F0F] text-[#F30F0F] hover:bg-[#FFF8F8] py-1 px-2 rounded-md transition-colors w-auto mt-1.5 font-medium"
          onClick={(e) => {
            e.stopPropagation();
            handleSubcategoryVendorChange(subcategory);
          }}
        >
          {subcategory?.vendor ? "Change Brand" : "Select Brand"}
        </button>
      </div>
    );
  };

  return (
    <div className="py-3  ">
      {/* Simple brand selection header - with padding */}
      <div className="px-4 mb-4">
  <h3 className="text-lg font-semibold text-gray-800 flex items-center">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3C5DB1]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
    </svg>
    Choose Brands
  </h3>
</div>

      
      {/* Regular Doors Section - full width border */}
      {doorSubcatsWithVendor.length > 0 && (
        <div className="mb-6 border-t bg-white border-b border-gray-200 w-full rounded-lg shadow-sm">
          {/* Section header with padding */}
          <div className="px-4 py-3">
            <h3 className="text-base font-semibold text-[#3C5DB1] flex items-center">
              Only Doors
            </h3>
          </div>
          
          {/* Horizontal scrollable row of brand cards with padding */}
          <div className="overflow-x-auto pb-3 px-4">
            <div className="flex space-x-4">
              {doorSubcatsWithVendor.map(subcategory => renderSubcategoryCard(subcategory))}
            </div>
          </div>
        </div>
      )}
      
      {/* Pre-Engineered Doors Section - full width border */}
      {preEngDoorSubcatsWithVendor.length > 0 && (
        <div className="border-t border-b border-gray-200 w-full">
          {/* Section header with padding */}
          <div className="px-4 py-3">
            <h3 className="text-base font-semibold text-gray-800 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3C5DB1]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Premium Doors
            </h3>
          </div>
          
          {/* Horizontal scrollable row of brand cards with padding */}
          <div className="overflow-x-auto pb-3 px-4">
            <div className="flex space-x-4">
              {preEngDoorSubcatsWithVendor.map(subcategory => renderSubcategoryCard(subcategory))}
            </div>
          </div>
        </div>
      )}
      
      {/* Simple empty state - with padding */}
      {doorSubcatsWithVendor.length === 0 && preEngDoorSubcatsWithVendor.length === 0 && (
        <div className="px-4">
          <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
            <p>No door brands available</p>
          </div>
        </div>
      )}

      {/* Vendor Selection Popup */}
      <VendorSelectionPopup
        isOpen={vendorsListPopupOpen}
        onClose={() => setVendorsListPopupOpen(false)}
        subcategory={selectedSubcategory}
        vendorsList={subcategoryVendorsList}
        onSelectVendor={handleSelectVendor}
        isLoading={isLoadingVendors}
      />
    </div>
  );
}

export default BrandCustomizationSection;
